import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

/**
 * Site input shape — the V4 sites list the quote form works with. The Mongo
 * id is what the legacy create/update route and the quote payload expect.
 */
interface SiteData {
  mongoSiteId: string;
  name?: string;
  addressLine1?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  tags?: string[];
}

interface MiniSiteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  site?: SiteData | null;
  tag?: string;
  onSaved: (siteId: string, site: Record<string, unknown>) => void;
}

export function MiniSiteFormDialog({
  open,
  onOpenChange,
  customerId,
  site,
  tag,
  onSaved,
}: MiniSiteFormDialogProps) {
  const { t } = useTranslation('quotes');
  const isEditing = !!site?.mongoSiteId;

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (site) {
        setName(site.name || '');
        setAddress(site.addressLine1 || '');
        setCity(site.city || '');
        setStateProvince(site.stateProvince || '');
        setPostalCode(site.postalCode || '');
        setCountry(site.country || '');
        setPhone(site.phone || '');
        setEmail(site.email || '');
      } else {
        setName('');
        setAddress('');
        setCity('');
        setStateProvince('');
        setPostalCode('');
        setCountry('');
        setPhone('');
        setEmail('');
      }
    }
  }, [open, site]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    try {
      // The legacy sites route writes the Mongo document directly, so the
      // payload must use the Mongo field names (`state_province_county`,
      // `postcode`) — anything else is silently discarded by the schema.
      const payload: Record<string, unknown> = {
        name: name.trim(),
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state_province_county: stateProvince.trim() || undefined,
        postcode: postalCode.trim() || undefined,
        country: country.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      };
      // Same fields in the V4 vocabulary, for the address the modal composes.
      const savedFields = {
        name: name.trim(),
        addressLine1: address.trim() || undefined,
        city: city.trim() || undefined,
        stateProvince: stateProvince.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        country: country.trim() || undefined,
      };

      if (isEditing) {
        const response = await api.put<{ site: { _id: string } }>(`/v4/legacy/sites/${site!.mongoSiteId}`, payload);
        onSaved(response.site._id, savedFields);
        toast.success(t('miniForm.editSite'));
      } else {
        const createPayload = {
          ...payload,
          organisation: customerId,
          tags: tag ? [tag] : [],
        };
        const response = await api.post<{ site: { _id: string } }>('/v4/legacy/sites', createPayload);
        onSaved(response.site._id, savedFields);
        toast.success(t('miniForm.createSite'));
      }
      onOpenChange(false);
    } catch {
      toast.error(isEditing ? t('miniForm.editSite') : t('miniForm.createSite'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('miniForm.editSite') : t('miniForm.createSite')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditing ? t('miniForm.editSite') : t('miniForm.createSite')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t('miniForm.siteName')} *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>{t('miniForm.address')}</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('miniForm.city')}</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('miniForm.stateProvince')}</Label>
              <Input value={stateProvince} onChange={(e) => setStateProvince(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('miniForm.postalCode')}</Label>
              <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('miniForm.country')}</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('miniForm.phone')}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('miniForm.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('form.cancel')}
            </Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? t('miniForm.saving') : t('miniForm.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
